import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Mail,
  Bell,
  BellRing,
  Shield,
  Database,
  Save,
  Globe,
  Users
} from 'lucide-react';
import { getApiUrl } from '../../config/api';
import NotificationSettings from './NotificationSettings';
import NotificationPreferences from './NotificationPreferences';
import { User } from '../../types';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface SettingsData {
  // General Settings
  appName: string;
  companyName: string;
  supportEmail: string;

  // Notification Settings
  emailNotifications: boolean;
  deploymentNotifications: boolean;
  approvalNotifications: boolean;
  rejectionNotifications: boolean;
  teamNotifications: boolean; // Enable/disable team member notifications
  notifyHeadOfOperations: boolean;
  headOfOperationsEmails: string[]; // Changed to array for multiple emails

  // Email Configuration
  emailFrom: string;

  // Deployment Settings
  deploymentWindow: string;
}

// Only these keys are honored by the server. Timezone, SMTP, approval policy,
// session timeout, password policy and 2FA are handled by Keycloak/SendGrid,
// so they are deliberately not editable here.
const SETTINGS_KEYS: (keyof SettingsData)[] = [
  'appName', 'companyName', 'supportEmail', 'emailFrom',
  'emailNotifications', 'deploymentNotifications', 'approvalNotifications',
  'rejectionNotifications', 'teamNotifications', 'notifyHeadOfOperations',
  'headOfOperationsEmails', 'deploymentWindow'
];

const pickSettings = (source: Partial<SettingsData> | null | undefined): Partial<SettingsData> => {
  const out: Partial<SettingsData> = {};
  if (!source || typeof source !== 'object') return out;
  SETTINGS_KEYS.forEach(key => {
    if (source[key] !== undefined) (out as any)[key] = source[key];
  });
  return out;
};

interface SettingsProps {
  currentUser?: User;
}

const Settings: React.FC<SettingsProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [settings, setSettings] = useState<SettingsData>({
    // General Settings
    appName: 'Deployment',
    companyName: 'PSG Software Technologies',
    supportEmail: 'deploy-production@psgsoftwaretechnologies.com',

    // Notification Settings
    emailNotifications: true,
    deploymentNotifications: true,
    approvalNotifications: true,
    rejectionNotifications: true,
    teamNotifications: true, // Default: enabled
    notifyHeadOfOperations: true,
    headOfOperationsEmails: ['head-operations@psgsoftwaretechnologies.com'],

    // Email Configuration
    emailFrom: 'deploy-production@psgsoftwaretechnologies.com',

    // Deployment Settings
    deploymentWindow: 'anytime',
  });

  useEffect(() => {
    const loadLocalFallback = () => {
      try {
        const savedSettings = localStorage.getItem('appSettings');
        if (savedSettings) {
          setSettings(prev => ({ ...prev, ...pickSettings(JSON.parse(savedSettings)) }));
        }
      } catch {
        // ignore unreadable local copy
      }
    };

    // Load settings from backend API
    const loadSettings = async () => {
      try {
        const response = await fetch(getApiUrl('api/settings'));
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({
            ...prev,
            ...pickSettings(data),
            // Ensure arrays are properly handled
            headOfOperationsEmails: Array.isArray(data?.headOfOperationsEmails) && data.headOfOperationsEmails.length > 0
              ? data.headOfOperationsEmails
              : prev.headOfOperationsEmails
          }));
        } else {
          toast(await readApiError(response, 'Failed to load settings'), 'error');
          loadLocalFallback();
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast('Failed to load settings from the server', 'error');
        loadLocalFallback();
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send the settings the server actually honors
      const backendSettings = pickSettings(settings);

      // Save to backend API
      const response = await fetch(getApiUrl('api/settings'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendSettings),
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to save settings to server'));
      }

      try {
        localStorage.setItem('appSettings', JSON.stringify(backendSettings));
      } catch {
        // local cache is best-effort
      }

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast(`Error saving settings: ${errorMessage}`, 'error');
      setSaveMessage('Error saving settings: ' + errorMessage);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'user-preferences', label: 'My Preferences', icon: Users },
    { id: 'push-notifications', label: 'Push Notifications', icon: BellRing },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'deployment', label: 'Deployment', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <SettingsIcon className="w-6 h-6 mr-2" />
          System Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage your deployment system configuration</p>
      </div>

      {saveMessage && (
        <div className={`mb-4 p-4 rounded-lg ${
          saveMessage.includes('Error')
            ? 'bg-red-100 text-red-700 border border-red-200'
            : 'bg-green-100 text-green-700 border border-green-200'
        }`}>
          {saveMessage}
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar Tabs */}
        <div className="w-64 bg-white rounded-lg shadow-md p-4 h-fit">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-6">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
                  <Globe className="w-5 h-5 mr-2" />
                  General Settings
                </h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Application Name
                </label>
                <input
                  type="text"
                  value={settings.appName}
                  onChange={(e) => setSettings({ ...settings, appName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Email
                </label>
                <input
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
                  <Bell className="w-5 h-5 mr-2" />
                  Notification Settings
                </h2>
                <p className="text-gray-600 text-sm">Configure when and how users receive notifications</p>
              </div>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Email Notifications</div>
                    <div className="text-sm text-gray-500">Enable all email notifications</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Deployment Notifications</div>
                    <div className="text-sm text-gray-500">Notify when deployments are completed</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.deploymentNotifications}
                    onChange={(e) => setSettings({ ...settings, deploymentNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Approval Notifications</div>
                    <div className="text-sm text-gray-500">Notify TLs and PMs when approval is needed</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.approvalNotifications}
                    onChange={(e) => setSettings({ ...settings, approvalNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Rejection Notifications</div>
                    <div className="text-sm text-gray-500">Notify requestors when deployments are rejected</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.rejectionNotifications}
                    onChange={(e) => setSettings({ ...settings, rejectionNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Team Member Notifications</div>
                    <div className="text-sm text-gray-500">Notify developers assigned to the same modules when deployments are created</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.teamNotifications}
                    onChange={(e) => setSettings({ ...settings, teamNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div>
                    <div className="font-medium text-gray-900">Notify Head of Operations</div>
                    <div className="text-sm text-gray-500">Send deployment completion emails to Head of Operations</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifyHeadOfOperations}
                    onChange={(e) => setSettings({ ...settings, notifyHeadOfOperations: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                {settings.notifyHeadOfOperations && (
                  <div className="ml-4 mt-2 space-y-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Head of Operations Emails
                    </label>
                    {settings.headOfOperationsEmails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const newEmails = [...settings.headOfOperationsEmails];
                            newEmails[index] = e.target.value;
                            setSettings({ ...settings, headOfOperationsEmails: newEmails });
                          }}
                          placeholder="head-operations@company.com"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {settings.headOfOperationsEmails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newEmails = settings.headOfOperationsEmails.filter((_, i) => i !== index);
                              setSettings({ ...settings, headOfOperationsEmails: newEmails });
                            }}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-300 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setSettings({
                          ...settings,
                          headOfOperationsEmails: [...settings.headOfOperationsEmails, '']
                        });
                      }}
                      className="mt-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-300 transition-colors text-sm font-medium"
                    >
                      + Add Another Email
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Add multiple email addresses to notify multiple Head of Operations members
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Notification Preferences */}
          {activeTab === 'user-preferences' && currentUser && (
            <NotificationPreferences currentUser={currentUser} />
          )}

          {/* Push Notifications */}
          {activeTab === 'push-notifications' && currentUser && (
            <NotificationSettings currentUser={currentUser} />
          )}

          {/* Email Configuration */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
                  <Mail className="w-5 h-5 mr-2" />
                  Email Configuration
                </h2>
                <p className="text-gray-600 text-sm">Configure the sender address used for notification emails</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Email Address
                </label>
                <input
                  type="email"
                  value={settings.emailFrom}
                  onChange={(e) => setSettings({ ...settings, emailFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="noreply@example.com"
                />
              </div>


              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Emails are delivered through SendGrid; the API key and delivery settings are configured in the server environment, not here.
                </p>
              </div>
            </div>
          )}

          {/* Deployment Settings */}
          {activeTab === 'deployment' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
                  <Database className="w-5 h-5 mr-2" />
                  Deployment Settings
                </h2>
                <p className="text-gray-600 text-sm">Configure when deployments are allowed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deployment Window
                </label>
                <select
                  value={settings.deploymentWindow}
                  onChange={(e) => setSettings({ ...settings, deploymentWindow: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="anytime">Anytime</option>
                  <option value="business-hours">Business Hours Only (9 AM - 6 PM IST)</option>
                  <option value="off-hours">Off Hours Only (6 PM - 9 AM IST)</option>
                  <option value="weekends">Weekends Only</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Enforced by the server for normal deploys: a deploy outside this window is refused.
                  Emergency deploys bypass the window.
                </p>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-900">
                  <Shield className="w-5 h-5 mr-2" />
                  Security Settings
                </h2>
                <p className="text-gray-600 text-sm">Manage authentication and security policies</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Password policy, session timeout and two-factor authentication are managed in
                  <strong> Keycloak</strong> (realm settings in the Keycloak admin console), not in this application.
                </p>
              </div>
            </div>
          )}

          {/* Save Button (only on tabs whose fields it saves) */}
          {['general', 'notifications', 'email', 'deployment'].includes(activeTab) && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
